/**
 * AnwaltPortal — Eigenes Dashboard für Anwalt-Partner
 * 
 * DESIGN: Professionell, seriös, minimalistisch. Dunkelblau Akzente.
 * Separates Layout ohne SV-Büro-Navigation.
 * Anwalt sieht NUR seine eigenen freigeschalteten Fälle.
 * 
 * Features v1:
 * - E-Mail-Login (Supabase Auth)
 * - Dashboard mit Fallübersicht
 * - Fälle-Liste mit Status und Dokumenten
 * - Dokument-Upload pro Fall
 * - Profil-Bereich
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useMobileDetailBackHandler } from '@/hooks/useMobileBackHandler';
import {
  LayoutDashboard, FolderOpen, User, LogOut, Search,
  FileText, Download, Clock, CheckCircle2,
  AlertCircle, ChevronRight, Building2, Car,
  Shield, Phone, Mail, MapPin, Loader2, ArrowLeft, Eye,
  FileCheck, Calendar, X, Lock, KeyRound, Upload,
  Scale, Briefcase, Gavel, FileUp, MessageSquare
} from 'lucide-react';

// ============ TYPES ============
interface AnwaltFall {
  id: string;
  akten_nr: string;
  kunde_name: string;
  kennzeichen: string;
  unfalldatum: string | null;
  status: string;
  erstellt_am: string;
  notizen: string | null;
  dokumente_count: number;
}

interface AnwaltDokument {
  id: string;
  fall_id: string;
  name: string;
  typ: string;
  groesse: number;
  hochgeladen_am: string;
  hochgeladen_von: string;
  url: string;
}

// ============ ANWALT LOGIN ============
export function AnwaltLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Bitte E-Mail und Passwort eingeben.'); return; }
    setLoading(true);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (authError) {
      if (authError.message.includes('Invalid login')) {
        setError('E-Mail oder Passwort ist falsch.');
      } else {
        setError('Login fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.');
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Bitte geben Sie Ihre E-Mail-Adresse ein.'); return; }
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/anwalt/login?reset=true`,
    });
    setLoading(false);
    if (resetError) setError('Fehler beim Zurücksetzen.');
    else setResetSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Anwalt-Portal</h1>
          <p className="text-sm text-gray-500 mt-1">Kfz Gutachter Koop — Partner-Zugang</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {showReset ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-[#1e3a5f]" />
                <h2 className="text-lg font-semibold text-gray-900">Passwort zurücksetzen</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">Sie erhalten einen Link zum Zurücksetzen.</p>
              {resetSent ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-800 font-medium">E-Mail wurde gesendet!</p>
                  <button onClick={() => { setShowReset(false); setResetSent(false); }} className="text-sm text-[#1e3a5f] hover:underline mt-4">
                    Zurück zum Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"><AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><p className="text-sm text-red-700">{error}</p></div>}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kanzlei@beispiel.de"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] outline-none" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#1e3a5f] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#152d4a] transition-colors disabled:opacity-50">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Link senden'}
                  </button>
                  <button type="button" onClick={() => { setShowReset(false); setError(null); }} className="w-full text-sm text-gray-500 hover:text-gray-700">Zurück zum Login</button>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-[#1e3a5f]" />
                <h2 className="text-lg font-semibold text-gray-900">Anmelden</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">Melden Sie sich mit Ihren Kanzlei-Zugangsdaten an.</p>
              {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2"><AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><p className="text-sm text-red-700">{error}</p></div>}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kanzlei@beispiel.de"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] outline-none" />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full bg-[#1e3a5f] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#152d4a] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Anmelden
                </button>
                <button type="button" onClick={() => { setShowReset(true); setError(null); }} className="w-full text-sm text-gray-500 hover:text-[#1e3a5f] transition-colors">
                  Passwort vergessen?
                </button>
              </form>
            </>
          )}
        </div>
        <p className="text-xs text-gray-400 text-center mt-6">Kfz Gutachter Koop · Rahmedestraße 65 · 58507 Lüdenscheid</p>
      </div>
    </div>
  );
}

// ============ ANWALT PORTAL (Main) ============
type PortalTab = 'dashboard' | 'faelle' | 'profil';

export default function AnwaltPortal() {
  const { user, signOut } = useAuth();
  const [anwaltId, setAnwaltId] = useState<string | null>(null);
  const [anwaltName, setAnwaltName] = useState('');
  const [kanzleiName, setKanzleiName] = useState('');
  const [activeTab, setActiveTab] = useState<PortalTab>('dashboard');
  const [selectedFall, setSelectedFall] = useState<AnwaltFall | null>(null);
  const [faelle, setFaelle] = useState<AnwaltFall[]>([]);
  const [loading, setLoading] = useState(true);
  const [identLoading, setIdentLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile: Hardware-Back schließt Fall-Detail statt Seite zu verlassen
  useMobileDetailBackHandler(!!selectedFall, () => setSelectedFall(null));

  // Identify anwalt by user_id
  useEffect(() => {
    if (!user) return;
    const identify = async () => {
      // Try to find anwalt_partner by user_id
      let { data } = await supabase
        .from('anwalt_partner')
        .select('id, name, kanzlei')
        .eq('user_id', user.id)
        .eq('aktiv', true)
        .single();
      if (!data) {
        // Fallback: by email
        const { data: byEmail } = await supabase
          .from('anwalt_partner')
          .select('id, name, kanzlei')
          .eq('email', user.email)
          .eq('aktiv', true)
          .single();
        data = byEmail;
      }
      if (data) {
        setAnwaltId(data.id);
        setAnwaltName(data.name || '');
        setKanzleiName(data.kanzlei || '');
      }
      setIdentLoading(false);
    };
    identify();
  }, [user]);

  // Load cases
  useEffect(() => {
    if (!anwaltId) return;
    const loadFaelle = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('anwalt_faelle')
        .select('*')
        .eq('anwalt_id', anwaltId)
        .order('erstellt_am', { ascending: false });
      if (!error && data) setFaelle(data as AnwaltFall[]);
      setLoading(false);
    };
    loadFaelle();
  }, [anwaltId]);

  const filteredFaelle = useMemo(() => {
    if (!searchQuery) return faelle;
    const q = searchQuery.toLowerCase();
    return faelle.filter(f =>
      f.akten_nr?.toLowerCase().includes(q) ||
      f.kunde_name?.toLowerCase().includes(q) ||
      f.kennzeichen?.toLowerCase().includes(q)
    );
  }, [faelle, searchQuery]);

  const stats = useMemo(() => ({
    gesamt: faelle.length,
    offen: faelle.filter(f => f.status === 'offen' || f.status === 'in_bearbeitung').length,
    abgeschlossen: faelle.filter(f => f.status === 'abgeschlossen').length,
    dokumente: faelle.reduce((sum, f) => sum + (f.dokumente_count || 0), 0),
  }), [faelle]);

  if (identLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (!anwaltId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Kein Kanzlei-Zugang gefunden</h2>
          <p className="text-sm text-gray-500 mb-6">Ihr Account ist keiner Kanzlei zugeordnet. Bitte kontaktieren Sie das SV Büro Koop.</p>
          <div className="flex gap-3 justify-center">
            <a href="tel:+4923514329960" className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg text-sm hover:bg-[#152d4a]">
              <Phone className="w-4 h-4" /> Anrufen
            </a>
            <button onClick={() => signOut()} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ FALL DETAIL ============
  if (selectedFall) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => setSelectedFall(null)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 truncate">Akte {selectedFall.akten_nr}</h1>
            <p className="text-xs text-gray-500 truncate">{selectedFall.kunde_name} · {selectedFall.kennzeichen}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            selectedFall.status === 'abgeschlossen' ? 'bg-green-100 text-green-700' :
            selectedFall.status === 'in_bearbeitung' ? 'bg-blue-100 text-blue-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {selectedFall.status === 'abgeschlossen' ? 'Abgeschlossen' :
             selectedFall.status === 'in_bearbeitung' ? 'In Bearbeitung' : 'Offen'}
          </span>
        </header>

        <div className="max-w-3xl mx-auto p-4 space-y-4">
          {/* Fall-Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-[#1e3a5f]" />
              Fall-Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Akten-Nr.</span><p className="font-medium">{selectedFall.akten_nr}</p></div>
              <div><span className="text-gray-500">Mandant</span><p className="font-medium">{selectedFall.kunde_name}</p></div>
              <div><span className="text-gray-500">Kennzeichen</span><p className="font-medium">{selectedFall.kennzeichen}</p></div>
              <div><span className="text-gray-500">Unfalldatum</span><p className="font-medium">{selectedFall.unfalldatum ? new Date(selectedFall.unfalldatum).toLocaleDateString('de-DE') : '—'}</p></div>
              <div className="sm:col-span-2"><span className="text-gray-500">Erstellt am</span><p className="font-medium">{new Date(selectedFall.erstellt_am).toLocaleDateString('de-DE')}</p></div>
            </div>
          </div>

          {/* Notizen */}
          {selectedFall.notizen && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#1e3a5f]" />
                Notizen vom Gutachter
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedFall.notizen}</p>
            </div>
          )}

          {/* Dokumente */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#1e3a5f]" />
                Dokumente
              </h3>
              <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1e3a5f] text-white rounded-lg text-xs font-medium hover:bg-[#152d4a] cursor-pointer transition-colors">
                <Upload className="w-3.5 h-3.5" />
                Hochladen
                <input type="file" className="hidden" multiple onChange={async (e) => {
                  const files = e.target.files;
                  if (!files) return;
                  for (const file of Array.from(files)) {
                    const path = `anwalt/${anwaltId}/${selectedFall.id}/${Date.now()}_${file.name}`;
                    const { error } = await supabase.storage.from('dokumente').upload(path, file);
                    if (error) {
                      toast.error(`Fehler beim Hochladen: ${file.name}`);
                    } else {
                      toast.success(`${file.name} hochgeladen`);
                      // Insert document record
                      await supabase.from('anwalt_dokumente').insert({
                        fall_id: selectedFall.id,
                        name: file.name,
                        typ: file.type,
                        groesse: file.size,
                        hochgeladen_von: 'anwalt',
                        url: path,
                      });
                    }
                  }
                }} />
              </label>
            </div>
            <div className="space-y-2">
              {selectedFall.dokumente_count === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Noch keine Dokumente vorhanden</p>
              ) : (
                <p className="text-sm text-gray-500">{selectedFall.dokumente_count} Dokument(e) vorhanden</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ MAIN PORTAL ============
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
            <Scale className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900">Kfz Gutachter Koop</h1>
            <p className="text-xs text-gray-500">Anwalt-Portal</p>
          </div>
        </div>
        <button onClick={() => signOut()} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500" title="Abmelden">
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {activeTab === 'dashboard' && (
          <div className="max-w-4xl mx-auto p-4 space-y-4">
            {/* Begrüßung */}
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] rounded-2xl p-5 text-white">
              <p className="text-sm opacity-80">Willkommen zurück,</p>
              <h2 className="text-xl font-bold mt-1">{anwaltName || 'Kanzlei'}</h2>
              {kanzleiName && <p className="text-sm opacity-70 mt-1">{kanzleiName}</p>}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-[#1e3a5f]">{stats.gesamt}</p>
                <p className="text-xs text-gray-500 mt-1">Fälle gesamt</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.offen}</p>
                <p className="text-xs text-gray-500 mt-1">Offen</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.abgeschlossen}</p>
                <p className="text-xs text-gray-500 mt-1">Abgeschlossen</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-gray-700">{stats.dokumente}</p>
                <p className="text-xs text-gray-500 mt-1">Dokumente</p>
              </div>
            </div>

            {/* Aktuelle Fälle */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Gavel className="w-4 h-4 text-[#1e3a5f]" />
                Aktuelle Fälle
              </h3>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#1e3a5f]" /></div>
              ) : faelle.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Noch keine Fälle freigeschaltet</p>
              ) : (
                <div className="space-y-2">
                  {faelle.slice(0, 5).map(fall => (
                    <button key={fall.id} onClick={() => { setSelectedFall(fall); }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-100">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${
                        fall.status === 'abgeschlossen' ? 'bg-green-500' :
                        fall.status === 'in_bearbeitung' ? 'bg-blue-500' : 'bg-amber-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{fall.kunde_name}</p>
                        <p className="text-xs text-gray-500">{fall.akten_nr} · {fall.kennzeichen}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </button>
                  ))}
                  {faelle.length > 5 && (
                    <button onClick={() => setActiveTab('faelle')} className="w-full text-sm text-[#1e3a5f] font-medium py-2 hover:underline">
                      Alle {faelle.length} Fälle anzeigen
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Kontakt */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Kontakt SV Büro</h3>
              <div className="space-y-2 text-sm">
                <a href="tel:+4923514329960" className="flex items-center gap-2 text-gray-600 hover:text-[#1e3a5f]">
                  <Phone className="w-4 h-4" /> 02351 / 432 99 60
                </a>
                <a href="mailto:info@gutachter-koop.de" className="flex items-center gap-2 text-gray-600 hover:text-[#1e3a5f]">
                  <Mail className="w-4 h-4" /> info@gutachter-koop.de
                </a>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" /> Rahmedestraße 65, 58507 Lüdenscheid
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'faelle' && (
          <div className="max-w-4xl mx-auto p-4 space-y-4">
            {/* Suche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Akten-Nr., Name oder Kennzeichen suchen..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] outline-none" />
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1e3a5f]" /></div>
            ) : filteredFaelle.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Keine Fälle gefunden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredFaelle.map(fall => (
                  <button key={fall.id} onClick={() => setSelectedFall(fall)}
                    className="w-full bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-[#1e3a5f]/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{fall.kunde_name}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            fall.status === 'abgeschlossen' ? 'bg-green-100 text-green-700' :
                            fall.status === 'in_bearbeitung' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {fall.status === 'abgeschlossen' ? 'Abgeschlossen' :
                             fall.status === 'in_bearbeitung' ? 'In Bearbeitung' : 'Offen'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{fall.akten_nr} · {fall.kennzeichen}</p>
                        {fall.unfalldatum && <p className="text-xs text-gray-400 mt-0.5">Unfall: {new Date(fall.unfalldatum).toLocaleDateString('de-DE')}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {fall.dokumente_count > 0 && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <FileText className="w-3.5 h-3.5" /> {fall.dokumente_count}
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profil' && (
          <div className="max-w-md mx-auto p-4 space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-white">
                  {anwaltName ? anwaltName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'AN'}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{anwaltName || 'Anwalt'}</h3>
              {kanzleiName && <p className="text-sm text-gray-500">{kanzleiName}</p>}
              <p className="text-xs text-gray-400 mt-1">{user?.email}</p>
            </div>

            <button onClick={() => signOut()}
              className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-colors">
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex items-center justify-around py-2 z-30"
        style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
        {([
          { id: 'dashboard' as PortalTab, icon: LayoutDashboard, label: 'Dashboard' },
          { id: 'faelle' as PortalTab, icon: FolderOpen, label: 'Fälle' },
          { id: 'profil' as PortalTab, icon: User, label: 'Profil' },
        ]).map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-lg transition-colors ${
              activeTab === tab.id ? 'text-[#1e3a5f]' : 'text-gray-400 hover:text-gray-600'
            }`}>
            <tab.icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
